import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, CheckCircle2, XCircle, Clock, FileText, Eye, RotateCcw } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { useNavigate } from 'react-router-dom';

export interface ReleaseTask {
  id: string;
  batchNumber: string;
  status: 'pending' | 'passed' | 'failed';
  result: string;
  createdBy: string;
  createdAt: string;
  updatedAt?: string;
  totalItems: number;
  passedItems: number;
}

const mapResultToStatus = (result: string): ReleaseTask['status'] => {
  const value = (result || '').toLowerCase();

  if (value.includes('pass') || value.includes('ok')) {
    return 'passed';
  }

  if (value.includes('fail') || value.includes('reject') || value.includes('ng')) {
    return 'failed';
  }

  return 'pending';
};

const mapServerTask = (item: any): ReleaseTask => {
  const status = mapResultToStatus(item?.result ?? '');
  const totalItems = 1;
  const passedItems = status === 'passed' ? 1 : 0;

  return {
    id: String(item?.id ?? ''),
    batchNumber: item?.batchNumber || '-',
    status,
    result: item?.result || '-',
    createdBy: item?.createdBy || '-',
    createdAt: item?.createdAt || new Date().toISOString(),
    updatedAt: item?.updatedAt,
    totalItems,
    passedItems,
  };
};

const formatNzDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

interface ReleaseTasksPageProps {
  tasks?: ReleaseTask[];
  onCreateNew?: () => void;
  onViewTask?: (taskId: string) => void;
}

export default function ReleaseTasksPage({ tasks = [], onCreateNew, onViewTask }: ReleaseTasksPageProps) {
  const navigate = useNavigate();
  const [serverTasks, setServerTasks] = useState<ReleaseTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [batchNumberFilter, setBatchNumberFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const fetchAllReleaseTasks = useCallback(async () => {
    setLoadingTasks(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setServerTasks([]);
        navigate('/login');
        return;
      }

      const perPage = 200;
      let start = 0;
      let totalCount = Number.POSITIVE_INFINITY;
      const merged: ReleaseTask[] = [];

      while (start < totalCount) {
        const end = start + perPage - 1;
        const params = new URLSearchParams();
        params.set('range', `[${start},${end}]`);
        params.set('perPage', String(perPage));

        const res = await fetch(`${API_URL}/ReleaseTasks?${params.toString()}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
          localStorage.removeItem('username');
          setServerTasks([]);
          navigate('/login');
          return;
        }

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const pageItems = (await res.json()) || [];
        const mappedItems = pageItems.map(mapServerTask);
        merged.push(...mappedItems);

        const contentRange = res.headers.get('Content-Range') || '';
        const totalMatch = /\/(\d+)$/.exec(contentRange);
        totalCount = totalMatch ? parseInt(totalMatch[1], 10) : merged.length;

        if (pageItems.length < perPage) {
          break;
        }

        start += perPage;
      }

      setServerTasks(merged);
    } catch (error) {
      console.error('Failed to load release tasks', error);
      setServerTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    fetchAllReleaseTasks();
  }, [fetchAllReleaseTasks]);

  const displayTasks = useMemo(() => {
    if (tasks.length > 0) {
      return tasks;
    }

    return serverTasks;
  }, [tasks, serverTasks]);

  const filteredTasks = useMemo(() => {
    const batchKeyword = batchNumberFilter.trim().toLowerCase();

    return displayTasks.filter((task) => {
      const matchesBatch = !batchKeyword || task.batchNumber.toLowerCase().includes(batchKeyword);

      return matchesBatch;
    });
  }, [batchNumberFilter, displayTasks]);

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / pageSize));
  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredTasks.slice(startIndex, startIndex + pageSize);
  }, [currentPage, filteredTasks, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [batchNumberFilter]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
      return;
    }

    navigate('/coa/release-tasks/create');
  };

  const handleViewTask = (task: ReleaseTask) => {
    if (onViewTask) {
      onViewTask(task.id);
      return;
    }

    navigate(
      `/coa/release-tasks/create?taskId=${encodeURIComponent(task.id)}&batchNumber=${encodeURIComponent(task.batchNumber)}&mode=view`
    );
  };

  const handleRetestTask = (task: ReleaseTask) => {
    navigate(
      `/coa/release-tasks/create?batchNumber=${encodeURIComponent(task.batchNumber)}&mode=retest&sourceTaskId=${encodeURIComponent(task.id)}`
    );
  };

  const getStatusIcon = (status: ReleaseTask['status']) => {
    switch (status) {
      case 'passed':
        return <CheckCircle2 className="size-5 text-green-500" />;
      case 'failed':
        return <XCircle className="size-5 text-red-500" />;
      case 'pending':
        return <Clock className="size-5 text-amber-500" />;
    }
  };

  const getStatusText = (status: ReleaseTask['status']) => {
    switch (status) {
      case 'passed':
        return 'Passed';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
    }
  };

  const getStatusBadge = (status: ReleaseTask['status']) => {
    switch (status) {
      case 'passed':
        return 'bg-green-100 text-green-700 border border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border border-red-200';
      case 'pending':
        return 'bg-amber-100 text-amber-700 border border-amber-200';
    }
  };

  const getVisiblePages = () => {
    const pagesToShow = 5;

    if (totalPages <= pagesToShow) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - pagesToShow + 1));
    return Array.from({ length: pagesToShow }, (_, index) => startPage + index);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl mb-1">Release Task List</h2>
          <p className="text-sm text-gray-500">Manage and review all quality release tasks</p>
        </div>
        <Button onClick={handleCreateNew} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="size-4 mr-2" />
          New Release Task
        </Button>
      </div>

      {loadingTasks ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <h3 className="text-lg text-gray-600 mb-2">Loading Release Tasks...</h3>
          <p className="text-sm text-gray-400">Fetching full data from server</p>
        </div>
      ) : displayTasks.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 bg-slate-50 px-4 py-4">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Batch No.</label>
              <Input
                value={batchNumberFilter}
                onChange={(e) => setBatchNumberFilter(e.target.value)}
                placeholder="Search batch number"
                className="bg-white max-w-md"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Batch No.
                  </th>
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Test Result
                  </th>
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Updated At
                  </th>
                  <th className="px-6 py-4 text-right text-xs text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedTasks.map((task) => (
                  <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 font-mono">{task.batchNumber}</span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewTask(task);
                        }}
                        className="inline-flex items-center gap-2"
                      >
                        {getStatusIcon(task.status)}
                        <span className={`px-3 py-1 rounded-full text-xs ${getStatusBadge(task.status)}`}>
                          {getStatusText(task.status)}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {task.createdBy || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatNzDateTime(task.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatNzDateTime(task.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewTask(task);
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Eye className="size-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetestTask(task);
                          }}
                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        >
                          <RotateCcw className="size-4 mr-1" />
                          Retest
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredTasks.length === 0 && (
            <div className="border-t border-gray-100 px-6 py-12 text-center text-sm text-gray-500">
              No release tasks match the current search criteria.
            </div>
          )}

          <div className="flex flex-col gap-4 border-t border-gray-200 bg-slate-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <span>
                {filteredTasks.length === 0
                  ? 'No data'
                  : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredTasks.length)} of ${filteredTasks.length}`}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1 || loadingTasks}
                className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Previous Page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center h-8 px-2 mx-1">
                {getVisiblePages().map((pageNum) => (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`min-w-8 h-8 px-0.5 rounded text-sm transition-colors ${
                      currentPage === pageNum ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages || loadingTasks}
                className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Next Page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="size-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg text-gray-600 mb-2">No Release Tasks Yet</h3>
          <p className="text-sm text-gray-400 mb-6">Click the button above to create your first release task</p>
          <Button onClick={handleCreateNew} variant="outline">
            <Plus className="size-4 mr-2" />
            New Release Task
          </Button>
        </div>
      )}
    </div>
  );
}